import { useChartStore } from '@/stores/chartStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useChartStyleJsonCache } from '@/composables/useChartStyleJsonCache';
import { convertChartDataToXML } from '@/composables/convertChartDataToXML';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { cloneProxyObject } from '@/utils/cloneProxyObject';
import { PromiseCacheMap, TimeCacheMap } from '@/utils/cache-map';
import { ChartStyle } from '@/interfaces/chart';

const ONE_SECOND = 1000;
const MAX_CACHE_ENTRY_AGE = 20 * ONE_SECOND;
const REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY = 20 * ONE_SECOND;

const temporaryStyleCacheIdPromisesCache = new PromiseCacheMap();
const temporaryStyleCacheIdCache = new TimeCacheMap<string>(REMOVE_OLD_CACHE_ENTRIES_LOOP_FREQUENCY, MAX_CACHE_ENTRY_AGE);

async function doStyleCacheRequest(chartXML: string, chartStyleCopy: ChartStyle, isPermanentChartOrChartStyle: boolean) {
  const { chartCode } = useAuthentication();
  const { addEntryToChartStyleJsonCache } = useChartStyleJsonCache();

  const apiCommandCopy = isPermanentChartOrChartStyle ? 'createpermanentstyle' : 'createtempstyle';
  const chartCodeCopy = isPermanentChartOrChartStyle ? chartCode.value : 'null';
  let responseText: string;

  try {
    const response = await fetch('/stylecache/cache', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: apiCommandCopy,
        chartcode: chartCodeCopy,
        xml: chartXML
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    responseText = await response.text();

    window.sc3WorkbenchLogger.info('stylecache POST responseText', responseText);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to exchange chart/ChartStyle SharpCharts2 chart xml for a styleCacheId.`, e);

    throw new Error(`Failed to exchange chart/ChartStyle SharpCharts2 chart xml for a styleCacheId.`, { cause: e });
  }

  if (!responseText.trim()) {
    throw new Error(`Failed to exchange chart/ChartStyle SharpCharts2 chart xml for a styleCacheId - trimmed responseText is an empty string for cmd: ${apiCommandCopy} and chartCode: ${chartCodeCopy}.`);
  }

  const styleCacheId = extractStyleCacheId(responseText.trim());

  if (!styleCacheId) {
    throw new Error(`Failed to exchange chart/ChartStyle SharpCharts2 chart xml for a styleCacheId - trimmed responseText: ${responseText.trim()} is a non-empty string for cmd: ${apiCommandCopy} and chartCode: ${chartCodeCopy}, but is not a valid parse-able styleCacheId.`);
  }

  if (!isPermanentChartOrChartStyle) {
    temporaryStyleCacheIdCache.set(chartXML, styleCacheId);
  }

  addEntryToChartStyleJsonCache(styleCacheId, chartStyleCopy);

  return styleCacheId;
}

async function getStyleCacheId(chartXML: string, chartStyleCopy: ChartStyle, isPermanentChartOrChartStyle: boolean): Promise<string> {
  const { getPermanentStyleCacheIdFromCache } = useChartStyleJsonCache();

  const permanentStyleCacheId = getPermanentStyleCacheIdFromCache(chartStyleCopy);

  if (permanentStyleCacheId) {
    return permanentStyleCacheId;
  }

  if (isPermanentChartOrChartStyle) {
    return await doStyleCacheRequest(chartXML, chartStyleCopy, isPermanentChartOrChartStyle);
  }

  const cached = temporaryStyleCacheIdCache.get(chartXML, false);

  if (cached) {
    return cached;
  }

  let temporaryStyleCacheIdPromise = temporaryStyleCacheIdPromisesCache.get(chartXML, true);

  if (!temporaryStyleCacheIdPromise) {
    temporaryStyleCacheIdPromise = doStyleCacheRequest(chartXML, chartStyleCopy, isPermanentChartOrChartStyle);

    temporaryStyleCacheIdPromisesCache.set(chartXML, temporaryStyleCacheIdPromise);

    temporaryStyleCacheIdPromise.catch(e => {
      window.sc3WorkbenchLogger.error('Error while fetching temporary styleCacheId.', e);
    });
  }

  await temporaryStyleCacheIdPromise;

  const newlyCachedStyleCacheId = temporaryStyleCacheIdCache.get(chartXML, false);

  if (!newlyCachedStyleCacheId) {
    throw new Error('Failed to get temporary styleCacheId');
  }

  return newlyCachedStyleCacheId;
}

export async function getSharpCharts2ChartImageUrlData(isPermanentChartOrChartStyle: boolean): Promise<SharpCharts2ChartImageUrlData> {
  const chartStore = useChartStore();
  const { isLoggedIn } = useAuthentication();

  if (
    isPermanentChartOrChartStyle &&
    !isLoggedIn.value
  ) {
    throw new Error('Unable to exchange saved chart/ChartStyle SharpCharts2 chart xml for a styleCacheId - user is not logged in.');
  }

  const sharpCharts2ChartData = convertChartDataToXML();
  const chartStyleCopy = cloneProxyObject(chartStore.chartStyle);
  const styleCacheId = await getStyleCacheId(sharpCharts2ChartData.chartXML, chartStyleCopy, isPermanentChartOrChartStyle);

  return {
    ...sharpCharts2ChartData,
    i: styleCacheId
  };
}
